import type React from 'react';

import { ChecklistEvaluationKey } from 'types';
import type { EvaluationParams, ToleranceBound } from 'types';

import {
  ArrayPitchParamsEditor,
  ArrayPitchSummary,
  FirstYearProductionParamsEditor,
  FirstYearProductionSummary,
  SystemSizeParamsEditor,
  SystemSizeSummary,
} from './evaluationTemplateDescriptors';

export interface EvaluationTemplateValidation {
  validate: (params: EvaluationParams | undefined) => boolean;
  message: string;
}

export interface EvaluationTemplateDescriptor {
  key: ChecklistEvaluationKey;
  ParamsEditor: React.FC<{ params: EvaluationParams; onCommit: (next: EvaluationParams) => void }>;
  validations: EvaluationTemplateValidation[];
  Summary: React.FC<{ params: EvaluationParams | undefined }>;
}

const hasBothBounds = (bound: ToleranceBound | undefined): boolean =>
  bound?.minPct !== undefined && bound?.maxPct !== undefined;

// Every ChecklistEvaluationKey must have an entry; `null` means that kind has no
// template-authored params UI (e.g. serialValidation, solarEdgeOptimizerValidation).
export const evaluationTemplateRegistry: Record<ChecklistEvaluationKey, EvaluationTemplateDescriptor | null> = {
  [ChecklistEvaluationKey.systemSize]: {
    key: ChecklistEvaluationKey.systemSize,
    ParamsEditor: SystemSizeParamsEditor,
    validations: [
      {
        validate: (params) =>
          hasBothBounds(params?.tolerancesByFinanceType?.ppa) &&
          hasBothBounds(params?.tolerancesByFinanceType?.lease),
        message: 'PPA and Lease tolerance min/max are required for System Size evaluations.',
      },
    ],
    Summary: SystemSizeSummary,
  },
  [ChecklistEvaluationKey.firstYearProduction]: {
    key: ChecklistEvaluationKey.firstYearProduction,
    ParamsEditor: FirstYearProductionParamsEditor,
    validations: [
      {
        validate: (params) => hasBothBounds(params?.tolerancesByFinanceType?.default),
        message: 'Tolerance min/max are required for First-Year Production evaluations.',
      },
    ],
    Summary: FirstYearProductionSummary,
  },
  [ChecklistEvaluationKey.arrayPitch]: {
    key: ChecklistEvaluationKey.arrayPitch,
    ParamsEditor: ArrayPitchParamsEditor,
    validations: [
      {
        validate: (params) => params?.toleranceDegrees !== undefined,
        message: 'Tolerance degrees is required for Array Pitch evaluations.',
      },
      {
        validate: (params) => {
          if (params?.minDegrees === undefined || params.maxDegrees === undefined) {
            return true;
          }

          return params.minDegrees < params.maxDegrees;
        },
        message: 'Minimum must be less than maximum for Array Pitch evaluations.',
      },
    ],
    Summary: ArrayPitchSummary,
  },
  [ChecklistEvaluationKey.serialValidation]: null,
  [ChecklistEvaluationKey.solarEdgeOptimizerValidation]: null,
  [ChecklistEvaluationKey.systemLoss]: null,
};
